import { memo, useRef } from "react"

import { PlayerToken } from "./player-token"
import type { PitchPlayer } from "./use-pitch-state"

interface GridCellProps {
  x: number
  y: number
  player: PitchPlayer | undefined
  homeTeamId: string
  homeTeamHue: number
  awayTeamHue: number
  /** When set, renders a colored action-type flash overlay on this cell. */
  highlightColor?: string | null
  /** The ID of the player currently acting (for token glow). */
  actingPlayerId?: string | null
  /** When set, this player ID gets the gold breathing highlight glow. */
  highlightedPlayerId?: string | null
  /** When provided, clicking a player token calls this with the player ID and cell DOMRect. */
  onPlayerClick?: (playerId: string, rect: DOMRect) => void
}

/**
 * A single cell in the 26×15 Blood Bowl pitch grid.
 * Renders a PlayerToken when a player occupies this cell.
 * Optionally renders a colored action-type highlight overlay.
 * Passes isActing for breathing glow, isHighlighted for narrative hover glow.
 * Forwards player click events for the player inspection popover.
 */
export const GridCell = memo(function GridCell({
  x,
  y,
  player,
  homeTeamId,
  homeTeamHue,
  awayTeamHue,
  highlightColor,
  actingPlayerId,
  highlightedPlayerId,
  onPlayerClick,
}: GridCellProps) {
  const cellRef = useRef<HTMLDivElement>(null)

  function handleTokenClick() {
    if (onPlayerClick && player && cellRef.current) {
      onPlayerClick(player.playerId, cellRef.current.getBoundingClientRect())
    }
  }

  const isActing =
    !!player && !!actingPlayerId && player.playerId === actingPlayerId
  const isHighlighted =
    !!player && !!highlightedPlayerId && player.playerId === highlightedPlayerId

  const isHome = player ? player.teamId === homeTeamId : false
  const isLightSquare = (x + y) % 2 === 0

  return (
    <div
      ref={cellRef}
      className="relative"
      style={{
        border: "1px solid oklch(0.60 0.10 145 / 0.08)",
        background: isLightSquare
          ? "oklch(0.30 0.08 145 / 0.10)"
          : "transparent",
      }}
      data-x={x}
      data-y={y}
    >
      {/* Action-type highlight flash overlay */}
      {highlightColor && (
        <div
          className="pointer-events-none absolute inset-0 animate-pulse"
          style={{
            background: `${highlightColor}40`, // 25% opacity
            zIndex: 1,
          }}
          aria-hidden="true"
        />
      )}

      {player && (
        <PlayerToken
          playerId={player.playerId}
          abbr={player.abbr}
          teamSide={isHome ? "home" : "away"}
          status={player.status}
          teamHue={isHome ? homeTeamHue : awayTeamHue}
          isActing={isActing}
          isHighlighted={isHighlighted}
          onClick={onPlayerClick ? handleTokenClick : undefined}
        />
      )}
    </div>
  )
})
